import apiClient from '@/api/client';
import { useMutation, useQuery } from '@tanstack/react-query';

export interface ReportOptions {
  eventTypes: string[];
  groupByOptions: string[];
  entityTypes: string[];
}

export interface EventReportRow {
  userId?: number;
  userName?: string;
  businessId?: number;
  businessName?: string;
  date?: string;
  source?: string;
  [eventType: string]: string | number | undefined;
}

export interface EventReportResult {
  rows: EventReportRow[];
  totalRows: number;
  filters: {
    startDate: string;
    endDate: string;
    eventTypes: string[];
    groupBy: string[];
  };
}

export interface ReportFilters {
  startDate: string;
  endDate: string;
  eventTypes: string[];
  groupBy: string[];
  entityTypes?: string[];
  businessId?: number;
  userId?: number;
}

export const useReportOptions = () =>
  useQuery({
    queryKey: ['eventReport', 'options'],
    queryFn: async (): Promise<ReportOptions> => {
      const { data, error } = await apiClient.GET(
        '/admin/event-report/options',
      );
      if (error || !data) {
        throw new Error('Failed to fetch report options');
      }
      return {
        eventTypes: data.eventTypes ?? [],
        groupByOptions: data.groupByOptions ?? [],
        entityTypes: data.entityTypes ?? [],
      };
    },
  });

const buildReportQuery = (filters: ReportFilters) => {
  const query: Record<string, string> = {
    startDate: filters.startDate,
    endDate: filters.endDate,
    eventTypes: filters.eventTypes.join(','),
    groupBy: filters.groupBy.join(','),
  };
  if (filters.entityTypes && filters.entityTypes.length > 0) {
    query.entityTypes = filters.entityTypes.join(',');
  }
  if (filters.businessId) {
    query.businessId = String(filters.businessId);
  }
  if (filters.userId) {
    query.userId = String(filters.userId);
  }
  return query;
};

export const useGenerateReport = () =>
  useMutation({
    mutationFn: async (filters: ReportFilters): Promise<EventReportResult> => {
      const { data, error } = await apiClient.GET(
        '/admin/event-report/generate',
        { params: { query: buildReportQuery(filters) } },
      );
      if (error || !data) {
        throw new Error('Failed to generate report');
      }
      return data as EventReportResult;
    },
  });

export const useExportCsv = () =>
  useMutation({
    mutationFn: async (filters: ReportFilters): Promise<void> => {
      const { data, error, response } = await apiClient.GET(
        '/admin/event-report/export',
        {
          params: { query: buildReportQuery(filters) },
          parseAs: 'blob',
        },
      );
      if (error || !data) {
        throw new Error('Failed to export CSV');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const match = contentDisposition?.match(/filename="(.+)"/);
      const filename =
        match?.[1] ??
        `event-report_${filters.startDate}_${filters.endDate}.csv`;

      const url = window.URL.createObjectURL(data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
